import React, { useEffect } from 'react';
import classNames from 'classnames/bind';
import { oneOf } from 'prop-types';

import {
  Table, TableBody, TableCell, TableRow
} from '@one-thd/sui-atomic-components';
import { Image, Heading } from '@thd-olt-component-react/core-ui';

import paintModals from '../../../data/paint-modals';
import sharedStyles from '../paint-modal-content.module.scss';
import styles from './Brand.module.scss';

const { brands } = paintModals;
const cx = classNames.bind({ ...styles, ...sharedStyles });

const PERFORMANCE_LABEL = 'PERFORMANCE';
const COVERAGE_LABEL = 'COVERAGE';
const COVERAGE_PER_GALLON_LABEL = 'COVERAGE PER GALLON';
const DURABILITY_LABEL = 'DURABILITY';
const EASY_TO_CLEAN_LABEL = 'EASY TO CLEAN';
const STAIN_BLOCKING_LABEL = 'STAIN BLOCKING';
const RESISTS_FADING_CRACKING_PEELING_LABEL = 'RESISTS FADING, CRACKING, AND PEELING';
const MOLD_MILDEW_RESISTANT_LABEL = 'MOLD/MILDEW RESISTANT';
const VOC_LABEL = 'ODOR & VOC CONTENT';

export const Brand = ({ brandKey, type }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('brands.ready'); }, []);

  const brandData = brands[brandKey][type];
  const isInteriorPaint = type === 'Interior Paint';
  const isGliddenBrand = brandKey === 'PPG - Glidden';

  return (
    <div
      className={cx('brand-modal', 'paint-modal-content', 'sui-px-2')}
      data-component="PaintModalContentBrand"
    >
      <Heading
        underline
        className={cx('brand-modal__header')}
        title={`Brand Options (${type})`}
      />
      <p className="sui-w-full sui-pb-3">
        Our top paint brands side by side. Compare performance,
        durability, value, and more.
      </p>
      <div className="sui-max-h-full sui-max-w-full sui-overflow-auto">
        <Table unlined>
          <TableBody>
            <TableRow>
              <TableCell component="th" sticky="horizontal" />
              {brandData.map((brand) => {
                const {
                  image,
                  name
                } = brand;
                return (
                  <TableCell align="center" component="th" padding="none">
                    <Image
                      className={cx('brand__image')}
                      src={image}
                      alt={name}
                      key={name}
                      width={100}
                      height={50}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell component="th" sticky="horizontal">{PERFORMANCE_LABEL}</TableCell>
              {brandData.map((brand) => {
                const { name, performance } = brand;
                return (
                  <TableCell key={`${name}-performance`}>{performance}</TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell component="th" sticky="horizontal">{COVERAGE_LABEL}</TableCell>
              {brandData.map((brand) => {
                const { name, coverage } = brand;
                return (
                  <TableCell key={`${name}-coverage`}>{coverage}</TableCell>
                );
              })}
            </TableRow>
            {isGliddenBrand && (
              <TableRow>
                <TableCell component="th" sticky="horizontal">{COVERAGE_PER_GALLON_LABEL}</TableCell>
                {brandData.map((brand) => {
                  const { name, coveragePerGallon } = brand;
                  return (
                    <TableCell key={`${name}-coveragePerGallon`}>{coveragePerGallon}</TableCell>
                  );
                })}
              </TableRow>
            )}
            <TableRow>
              <TableCell component="th" sticky="horizontal">{DURABILITY_LABEL}</TableCell>
              {brandData.map((brand) => {
                const { name, durability } = brand;
                return (
                  <TableCell key={`${name}-durability`}>{durability}</TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell component="th" sticky="horizontal">
                {isInteriorPaint && isGliddenBrand ? EASY_TO_CLEAN_LABEL : MOLD_MILDEW_RESISTANT_LABEL}
              </TableCell>
              {brandData.map((brand) => {
                const { name, easyToClean, moldMildewResistant, washability } = brand;
                let content = washability;
                if (isGliddenBrand) {
                  content = isInteriorPaint ? easyToClean : moldMildewResistant;
                }
                return (
                  <TableCell key={`${name}-easyToClean`}>{content}</TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell component="th" sticky="horizontal">
                {isInteriorPaint && isGliddenBrand ? STAIN_BLOCKING_LABEL : RESISTS_FADING_CRACKING_PEELING_LABEL}
              </TableCell>
              {brandData.map((brand) => {
                const { name, stainBlocking, resistsFadingCrackingPeeling } = brand;
                let content = stainBlocking;
                if (isGliddenBrand) {
                  content = isInteriorPaint ? stainBlocking : resistsFadingCrackingPeeling;
                }
                return (
                  <TableCell key={`${name}-stainBlocking`}>{content}</TableCell>
                );
              })}
            </TableRow>
            {isInteriorPaint && (
              <TableRow>
                <TableCell component="th" sticky="horizontal">{VOC_LABEL}</TableCell>
                {brandData.map((brand) => {
                  const { name, odorVOCContent } = brand;
                  return (
                    <TableCell key={`${name}-odorVOCContent`}>{odorVOCContent}</TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className={cx('brand-modal__disclaimer', 'sui-pt-1')}>
        * Colorants added to this base may increase voc level significantly depending on color
        choices
      </div>
    </div>
  );
};

Brand.propTypes = {
  brandKey: oneOf(['Behr', 'PPG - Glidden']),
  type: oneOf((['Interior Paint', 'Exterior Paint']))
};

Brand.defaultProps = {
  brandKey: 'Behr',
  type: 'Interior Paint',
};

Brand.displayName = 'PaintModalContent.Brand';
